import { EmployeeLoyaltyProgramsRequest } from '~/services/BusinessTraveller/BusinessTravellerService';

import { LoyaltyProgramRow, LoyaltyProgramRowsState } from './loyaltyProgramsConfig';

/**
 * Initializes loyalty program rows from API data.
 * If no data exists, returns an array with one empty row.
 *
 * @param programs - Array of programs from API
 * @param type - Type of program ('flight' or 'hotel')
 * @returns Array of loyalty program rows ready for display
 */
export function initialiseLoyaltyProgramRows(
  programs: Array<LoyaltyProgramRow>,
  type: 'flight' | 'hotel',
): Array<LoyaltyProgramRow> {
  return programs.length > 0
    ? programs
    : [
        {
          id: crypto.randomUUID(),
          program: '',
          memberNumber: '',
          type,
        },
      ];
}

/**
 * Computes the final loyalty program rows for submission by comparing current rows with initial rows.
 * - Programs in both current and initial → Update (use current row data)
 * - Programs only in current → Create (new programs)
 * - Programs only in initial → Delete (send with empty memberNumber)
 *
 * @param rowsStates - Array of state objects containing initialRows and currentRows
 * @returns Array of loyalty programs ready for API submission
 */
export function computeFinalLoyaltyRows(
  ...rowsStates: Array<LoyaltyProgramRowsState>
): EmployeeLoyaltyProgramsRequest['employeeLoyaltyPrograms'] {
  // Filter only the rows with program selected, do not want to create record without program name
  const allCurrentRows = rowsStates.flatMap((state) => state.currentRows).filter((row) => row.program);
  const allInitialRows = rowsStates.flatMap((state) => state.initialRows).filter((row) => row.program);

  // Get programs from current rows (excluding empty program selections)
  const currentPrograms = allCurrentRows.reduce<Record<string, LoyaltyProgramRow>>((acc, row) => {
    acc[row.program] = row;
    return acc;
  }, {});

  // Get programs from initial rows
  const initialPrograms = allInitialRows.reduce<Record<string, LoyaltyProgramRow>>((acc, row) => {
    acc[row.program] = row;
    return acc;
  }, {});

  // Build final rows from current programs
  // Filter out rows with empty member number that were NOT in initial rows
  const createOrUpdateRows = Object.entries(currentPrograms)
    .filter(([program, row]) => {
      const hasMemberNumber = row.memberNumber.trim().length > 0;
      const existedInitially = program in initialPrograms;
      return hasMemberNumber || existedInitially;
    })
    .map(([, row]) => row);

  // Add deleted rows (programs in initial but not in current) with empty memberNumber
  const deletedRows = Object.entries(initialPrograms)
    .filter(([program]) => !(program in currentPrograms))
    .map(([, row]) => ({ ...row, memberNumber: '' }));

  const finalRows: Array<LoyaltyProgramRow> = [...createOrUpdateRows, ...deletedRows];

  return finalRows.map((row) => ({
    program: row.program,
    memberNumber: row.memberNumber.trim(),
    type: row.type,
  }));
}
